import React, { useEffect, useState } from "react";
import { IoCloseOutline } from "react-icons/io5";
import CustomButton from "../Form/CustomButton";
import useForm from "../../hooks/useForm";
import { COMMON_FIELD_TYPES } from "../../constants/fieldTypes";
import CustomInput from "../../components/Form/Input";
import DropDownNew from "../Form/DropDownNew";
import { areas, cities, FORM_MODES } from "../../constants/commonConstants";
import { useThunk } from "../../hooks/useThunk";
import { createProperty, editProperty } from "../../store/thunks/propertyThunks";
import Loader from "../Common/Loader";
import showToast from "../../utils/toastNotifications";

const AddPropertyModal = ({
  onClose,
  onSubmit,
  mode = "add", // 'add' or 'edit'
  defaultData = {},
  doCreateProperty,
  doEditProperty,
  isEditingProperty,
  isCreatingProperty
}) => {
  const isEdit = mode === "edit";

  const [selectedCity, setSelectedCity] = useState("");
  const [areaOptions, setAreaOptions] = useState([]);
  

  const {
    formData,
    errors,
    touched,
    handleInputChange,
   // handleBlur,
    clearError,
    validateForm,
    setFormData,
    setTouched,
  } = useForm(
    {
      propertyName: "",
      city: "",
      area: "",
      totalUnits: "1",
    },
    {
      propertyName: true,
      city: true,
      area: true,
      totalUnits: true,
    },
    {
      propertyName: COMMON_FIELD_TYPES.REQUIRED_FIELD,
      city: COMMON_FIELD_TYPES.DROPDOWN,
      area: COMMON_FIELD_TYPES.DROPDOWN,
      totalUnits: COMMON_FIELD_TYPES.REQUIRED_FIELD,
    }
  );

  useEffect(() => {
    if (isEdit && defaultData) {
      setFormData({
        propertyName: defaultData.propertyName || "",
        city: defaultData?.location[0]?.city || "",
        area: defaultData?.location[0]?.area || "",
        totalUnits: defaultData.totalUnits || "1",
      });
    }
  }, [isEdit, defaultData]);

  const handleBlur = (field, value) => {
    if (typeof value === "string") {
      formData[field] = value.trim();
    } else {
      formData[field] = value;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setTouched({
      propertyName: true,
      city: true,
      area: true,
      totalUnits: true,
    });

    if (!formData.totalUnits || parseInt(formData.totalUnits) <= 0) {
      errors.totalUnits = "Total Units must be greater than 0.";
      showToast('error', 'Total Units cannot be 0.');
      return;
    }
      Object.keys(formData).forEach((field) => handleBlur(field, formData[field]));

    if (validateForm()) {

      let result;
      
      if (mode === FORM_MODES.ADD) {
        result = await doCreateProperty(formData);
      }
      
      if (mode === FORM_MODES.EDIT) {
        result = await doEditProperty({ propertyData: formData, propertyId: defaultData._id.toString() });
      }

      if (result?.success) {
        onSubmit(formData); // Pass the form data after a successful operation
        showToast('success', isEdit ? 'Property updated successfully!' : 'Property added successfully!');
      } else {
        console.error('Error: Failed to submit form data', result?.error?.message || "Operation failed");
        showToast('error', result?.error?.message || 'Failed to submit form data');
      }

    } else {
      // console.log('formData is inValid')
      return; 
    }  

    let result;
  };

  const handleCityChange = (val) => {
    
    const selectedCity = val?.value || "";
  
    setSelectedCity(selectedCity);
    handleInputChange("city", selectedCity );
  
    const locationData = areas.find((item) => item.city === selectedCity);

    const formattedAreas = locationData
    ? locationData.area.map((area) => ({
        label: area,
        value: area,
      }))
    : [];

    setAreaOptions(formattedAreas);
    handleInputChange("area", "");
    clearError("area");
  };
  

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 ">
      {(isCreatingProperty || isEditingProperty ) && (
        <div className="flex justify-center items-center absolute z-50 inset-0 ">
          <Loader color='#873FE2'/>
        </div>
      )}
      <div className="bg-white shadow-lg rounded-lg w-[35rem] h-[400px] sm:h-[410px] md:h-[420px] lg:h-[450px] max-w-[90%] p-6 relative">
      
        <button
          onClick={onClose}
          className="absolute text-gray-500 top-4 right-4 hover:text-gray-800"
        >
          <IoCloseOutline size={28} />
        </button>

        <h2 className="text-[22px] w-full text-left font-semibold font-inter leading-[100%]  mb-6 text-[#4772F1]">
          {isEdit ? "Edit Property" : "Add Property"}
        </h2>

        <div className="space-y-4">
          <CustomInput
            type="text"
            name="propertyName"
            label="Property Name"
            value={formData.propertyName}
            error={touched.propertyName && errors.propertyName}
            onChange={(e) => handleInputChange("propertyName", e.target.value)}
            onBlur={() => handleBlur("propertyName", formData.propertyName)}
            onFocus={() => clearError("propertyName")}
            labelStyle=" text-[#071952] font-inter font-medium text-[14px] sm:text-[15px] md:text-[16px] lg:text-[17px] leading-[22px]"
            inputStyle="text-[9px] sm:text-[11px] md:text-[13px] lg:text-[15px]"
            className="
              inline-flex items-center flex-shrink-0 
              border border-gray-300 
              bg-primary-light/30 text-content 
              placeholder:text-[#9D9D9D] 
              h-8 sm:h-9 md:h-10 lg:h-[45px] 
              rounded-[5px] px-[13px] py-[14px]"
            isRequired
          />

          <div className="flex gap-4 relative top-[28px] ">
            <DropDownNew
              label="City"
              name="city"
              options={cities}
              defaultOption={
                formData.city
                  ? { label: formData.city, value: formData.city }
                  : null
              }
              onSelect={(val) => handleCityChange(val)}
              placeholder="City"
              displayKey="label"
              idKey="value"
              isForm
              isRequired
              error={touched.city && errors.city}
              allowFillColor
              buttonClassName="rounded-[5px] h-[32px] sm:h-[36px] md:h-[40px] lg:h-[45px]  w-full border border-[#81818180] relative top-[0px] text-[13px] placeholder:text-[12px] sm:placeholder:text-[13px] md:placeholder:text-[14px] lg:placeholder:text-[15px]"
              dropdownClassName="w-full rounded-[0px] mt-1 top-[57px] sm:top-[61px]  md:top-[65px] lg:top-[70px] border border-[#81818180]"
              labelStyle="-top-[30px] left-[-2px]  font-inter text-[#09114B] text-[11px] sm:text-[13px] md:text-[15px] lg:text-[17px] font-medium"
            />

            <DropDownNew
              label="Area"
              name="area"
              options={areaOptions}
              defaultOption={
                formData.area
                  ? { label: formData.area, value: formData.area }
                  : null
              }
              onSelect={(val) => handleInputChange("area", val?.value || "")}
              placeholder="Area"
              displayKey="label"
              idKey="value"
              isForm
              isRequired
              error={touched.area && errors.area}
              allowFillColor
              buttonClassName="rounded-[5px] h-[32px] sm:h-[36px] md:h-[40px] lg:h-[45px] w-full border border-[#81818180] text-[13px]"
              dropdownClassName="w-full rounded-[0px] mt-1 top-[57px] sm:top-[61px]  md:top-[65px] lg:top-[70px] border border-[#81818180]"
              labelStyle="-top-[30px] left-[-2px]  font-inter text-[#09114B] text-[11px] sm:text-[13px] md:text-[15px] lg:text-[17px] font-medium"
            />
          </div>
<div className="relative top-8">
          <CustomInput
            type="text"
            name="totalUnits"
            label="Total Units"
            value={formData.totalUnits}
            error={touched.totalUnits && errors.totalUnits}
            onChange={(e) => handleInputChange("totalUnits", e.target.value)}
            onBlur={() => handleBlur("totalUnits", formData.totalUnits)}
            onFocus={() => clearError("totalUnits")}
            labelStyle=" text-[#071952] font-inter font-medium text-[14px] sm:text-[15px] relative top-0 md:text-[16px] lg:text-[17px] leading-[22px]"
            inputStyle="text-[9px] sm:text-[11px] md:text-[13px] lg:text-[15px]"
            className="
              inline-flex items-center flex-shrink-0 relative top-0
              border border-gray-300 
              bg-primary-light/30 text-content 
              placeholder:text-[#9D9D9D] 
              h-8 sm:h-9 md:h-10 lg:h-[45px] 
              rounded-[5px] px-[13px] py-[14px]"
            isRequired
          /></div>

          <div className="relative flex items-center justify-center gap-4 mt-6 text-center top-10">
            <CustomButton
              buttonText="cancel"
              onClick={onClose}
              hoverEffect="none"
              className="border uppercase border-[#873FE2] text-[#873FE2] bg-transparent w-[73.16px] h-7 rounded-[3.77px] py-[6.5px] px-[12.08px] text-[12px] font-[500] font-inter leading-[100%] "
            />
            <CustomButton
              buttonText={isEdit ? "save changes" : "Add Property"}
              type="submit"
              onClick={handleSubmit}
              hoverEffect="none"
              className="bg-[#873FE2] text-white border-[#873FE2] w-[117.16px] h-7 rounded-[3.77px] py-[6.5px] uppercase px-[6px] text-[12px] font-[500] font-inter leading-[100%] "
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPropertyModal;
